/**
 * DistributionPreview.jsx
 *
 * Renders a smooth, analytical preview of the chosen temperature-sampling
 * distribution as a clean SVG area chart. Uses closed-form PDFs (not random
 * sampling) so the curve is always silky-smooth and deterministic.
 */
import { useMemo } from 'react';

const SQRT_2PI = Math.sqrt(2 * Math.PI);

// Standard-normal PDF
const normalPdf = (x, mu, sigma) =>
  Math.exp(-((x - mu) ** 2) / (2 * sigma * sigma)) / (sigma * SQRT_2PI);

// Beta(a,b) PDF — only used here with integer params, so factorial is fine
const factorial = (n) => {
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
};
const betaPdf = (x, a, b) => {
  if (x <= 0 || x >= 1) return 0;
  const B = (factorial(a - 1) * factorial(b - 1)) / factorial(a + b - 1);
  return (x ** (a - 1) * (1 - x) ** (b - 1)) / B;
};

// Triangular PDF on [0,1] with peak at c
const triangularPdf = (x, c = 0.5) => {
  if (x < 0 || x > 1) return 0;
  if (x < c) return (2 * x) / c;
  return (2 * (1 - x)) / (1 - c);
};

// Log-normal PDF on x>0, mapped to [0,1] via x => x*4 (visualisation only)
const lognormalPdf = (x, mu = 0, sigma = 0.5) => {
  if (x <= 0) return 0;
  return Math.exp(-((Math.log(x) - mu) ** 2) / (2 * sigma * sigma)) / (x * sigma * SQRT_2PI);
};

// Skew-normal (Owen): φ(z)·Φ(αz)·2; we approximate Φ via tanh for cheap rendering
const skewNormalPdf = (x, mu = 0.4, sigma = 0.18, alpha = 4) => {
  const z = (x - mu) / sigma;
  const phi = Math.exp(-(z * z) / 2) / SQRT_2PI;
  // Φ approximation
  const cdfApprox = 0.5 * (1 + Math.tanh(Math.sqrt(2 / Math.PI) * (alpha * z)));
  return (2 * phi * cdfApprox) / sigma;
};

// Sample the analytical PDF at N points across [0,1]
function pdfCurve(distribution, polarization = 0.5, N = 80) {
  const xs = Array.from({ length: N }, (_, i) => i / (N - 1));
  let raw;

  switch (distribution) {
    case 'uniform':
      raw = xs.map(() => 1);
      break;

    case 'normal':
      // tight bell centred at 0.5
      raw = xs.map((x) => normalPdf(x, 0.5, 0.14));
      break;

    case 'beta':
      raw = xs.map((x) => betaPdf(x, 2, 2));
      break;

    case 'bimodal': {
      // Two symmetric Gaussian peaks; spacing scales with polarization
      const sep = 0.18 + polarization * 0.18; // 0.18 → 0.36
      const mu1 = 0.5 - sep;
      const mu2 = 0.5 + sep;
      const sigma = 0.07;
      raw = xs.map((x) => 0.5 * normalPdf(x, mu1, sigma) + 0.5 * normalPdf(x, mu2, sigma));
      break;
    }

    case 'triangular':
      raw = xs.map((x) => triangularPdf(x, 0.5));
      break;

    case 'skewed_normal':
      raw = xs.map((x) => skewNormalPdf(x));
      break;

    case 'log_normal':
      // x*3 maps [0,1] → [0,3]; gives a nice right-tailed curve in the visible window
      raw = xs.map((x) => lognormalPdf(x * 3 + 0.05, 0, 0.5));
      break;

    case 'exponential': {
      const lambda = 3;
      raw = xs.map((x) => lambda * Math.exp(-lambda * x));
      break;
    }

    case 'discrete_step': {
      // Five gaussian bumps at 0.1, 0.3, 0.5, 0.7, 0.9
      const peaks = [0.1, 0.3, 0.5, 0.7, 0.9];
      const sigma = 0.025;
      raw = xs.map((x) => peaks.reduce((acc, mu) => acc + normalPdf(x, mu, sigma), 0));
      break;
    }

    case 'truncated_normal':
      raw = xs.map((x) => normalPdf(x, 0.5, 0.18));
      break;

    default:
      raw = xs.map((x) => normalPdf(x, 0.5, 0.14));
  }

  const max = Math.max(...raw, 1e-9);
  return raw.map((v) => v / max); // normalise to [0,1]
}

// Build a smooth Catmull-Rom-ish path from points (Bezier control points).
function smoothPath(points) {
  if (points.length < 2) return '';
  let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

const DIST_LABELS = {
  normal: 'Normal',
  uniform: 'Uniform',
  beta: 'Beta (2,2)',
  bimodal: 'Bimodal',
  triangular: 'Triangular',
  skewed_normal: 'Skewed Normal',
  log_normal: 'Log-Normal',
  exponential: 'Exponential',
  discrete_step: 'Discrete Step',
  truncated_normal: 'Truncated Normal',
};

const DIST_COLORS = {
  normal: '#3b82f6',
  uniform: '#8b5cf6',
  beta: '#f59e0b',
  bimodal: '#ef4444',
  triangular: '#10b981',
  skewed_normal: '#06b6d4',
  log_normal: '#f97316',
  exponential: '#ec4899',
  discrete_step: '#6366f1',
  truncated_normal: '#14b8a6',
};

export default function DistributionPreview({ distribution, polarization = 0.5 }) {
  const curve = useMemo(
    () => pdfCurve(distribution, polarization),
    [distribution, polarization]
  );

  const W = 280;
  const H = 80;
  const pad = { top: 6, bottom: 16, left: 4, right: 4 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;
  const color = DIST_COLORS[distribution] || '#3b82f6';

  const points = curve.map((v, i) => ({
    x: pad.left + (i / (curve.length - 1)) * plotW,
    y: pad.top + plotH * (1 - v),
  }));

  const lineD = smoothPath(points);
  const baselineY = pad.top + plotH;
  const areaD =
    `${lineD} L ${pad.left + plotW} ${baselineY} L ${pad.left} ${baselineY} Z`;

  return (
    <div
      className="rounded-lg border border-border bg-muted/20 p-2 mt-2"
      data-testid="distribution-preview"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
          Preview
        </span>
        <span
          className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: `${color}18`, color }}
        >
          {DIST_LABELS[distribution] || distribution}
        </span>
      </div>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block"
      >
        <defs>
          <linearGradient id={`grad-${distribution}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0.03} />
          </linearGradient>
        </defs>

        <path d={areaD} fill={`url(#grad-${distribution})`} />
        <path
          d={lineD}
          fill="none"
          stroke={color}
          strokeWidth={1.6}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        <line
          x1={pad.left}
          y1={baselineY}
          x2={pad.left + plotW}
          y2={baselineY}
          stroke="currentColor"
          className="text-border"
          strokeWidth={0.5}
        />

        <text x={pad.left} y={H - 2} fontSize={8} fill="currentColor" className="text-muted-foreground">
          Low
        </text>
        <text
          x={pad.left + plotW}
          y={H - 2}
          fontSize={8}
          fill="currentColor"
          className="text-muted-foreground"
          textAnchor="end"
        >
          High
        </text>
        <text
          x={pad.left + plotW / 2}
          y={H - 2}
          fontSize={8}
          fill="currentColor"
          className="text-muted-foreground"
          textAnchor="middle"
        >
          Temperature
        </text>
      </svg>
    </div>
  );
}
